/*
 * Promote the card images a mega-menu panel holds in data-* attributes.
 * They ship inert so they cannot burn pre-LCP bandwidth on a page load where
 * the menu is never opened; this runs on the first open and is a no-op after.
 */
function hydrateMegaMenuImages(root) {
  if (!root || root.dataset.imagesHydrated === 'true') return;
  root.dataset.imagesHydrated = 'true';
  root.querySelectorAll('img[data-mega-src]').forEach((img) => {
    const { megaSrcset, megaSizes, megaSrc } = img.dataset;
    if (megaSizes) img.sizes = megaSizes;
    if (megaSrcset) img.srcset = megaSrcset;
    if (megaSrc) img.src = megaSrc;
    delete img.dataset.megaSrc;
    delete img.dataset.megaSrcset;
    delete img.dataset.megaSizes;
  });
}

class BadassMegaMenu {
  constructor() {
    this.menus = document.querySelectorAll('header-menu > details.badass-mega-menu');
    this.dropdownMenus = document.querySelectorAll('header-menu > details.mega-menu:not(.badass-mega-menu)');
    if (!this.menus.length && !this.dropdownMenus.length) return;

    this.closeTimers = new WeakMap();
    this.supportsHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    this.menus.forEach((menu) => {
      this.initScroller(menu);

      /* First open — or first intent to open — pulls the card images in. */
      menu.addEventListener('toggle', () => {
        if (menu.hasAttribute('open')) hydrateMegaMenuImages(menu);
      });
      const summaryEl = menu.querySelector('summary');
      if (summaryEl) {
        ['pointerenter', 'focus', 'touchstart'].forEach((evt) =>
          summaryEl.addEventListener(evt, () => hydrateMegaMenuImages(menu), {
            once: true,
            passive: true,
          })
        );
      }

      const overlay = menu.querySelector('.badass-mega-menu__overlay');
      const summary = menu.querySelector('summary');

      if (overlay) {
        overlay.addEventListener('click', (event) => {
          if (event.target.closest('[data-mega-scroll], .badass-mega-menu__panel')) return;
          this.closeMenu(menu);
        });
        if (this.supportsHover) {
          overlay.addEventListener('mouseenter', () => this.scheduleClose(menu));
        }
      }

      if (summary) {
        summary.addEventListener('click', (event) => {
          if (!this.supportsHover || event.detail === 0) return;
          event.preventDefault();
          if (menu.hasAttribute('open')) {
            this.closeMenu(menu);
          } else {
            this.openMenu(menu);
          }
        });
      }

      if (this.supportsHover) {
        const viewport = menu.querySelector('.badass-mega-menu__viewport');
        const keepOpen = () => clearTimeout(this.closeTimers.get(menu));
        const tryClose = () => this.scheduleClose(menu);

        if (summary) {
          summary.addEventListener('mouseenter', () => this.openMenu(menu));
          summary.addEventListener('mouseleave', tryClose);
        }

        if (viewport) {
          const panel = menu.querySelector('.badass-mega-menu__panel');
          viewport.addEventListener('mouseenter', keepOpen);
          viewport.addEventListener('mouseleave', tryClose);
          if (panel) {
            panel.addEventListener('mouseenter', keepOpen);
            panel.addEventListener('mouseleave', tryClose);
            panel.addEventListener('pointerdown', keepOpen);
          }
        }
      }

      menu.addEventListener('toggle', () => {
        if (summary) {
          summary.setAttribute('aria-expanded', menu.open ? 'true' : 'false');
        }

        if (menu.open) {
          this.updateHeaderPosition();
          this.updateScroller(menu);
          this.closeOtherMenus(menu);
          this.setHeaderPreventHide(true);
          document.body.classList.add('badass-mega-menu-open');
        } else {
          this.setHeaderPreventHide(false);
          if (!this.anyMenuOpen()) {
            document.body.classList.remove('badass-mega-menu-open');
          }
        }
      });
    });

    this.initDropdownMenus();

    this.onScrollOrResize = () => {
      this.menus.forEach((menu) => {
        if (menu.open) this.updateHeaderPosition();
      });
    };

    window.addEventListener('scroll', this.onScrollOrResize, { passive: true });
    window.addEventListener('resize', this.onScrollOrResize);

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.menus.forEach((menu) => this.closeMenu(menu));
        this.dropdownMenus.forEach((menu) => this.closeDropdown(menu));
      }
    });

    this.initNavSwitching();
  }

  initNavSwitching() {
    if (!this.supportsHover) return;

    const navItems = document.querySelectorAll('.header__inline-menu .list-menu--inline > li');
    navItems.forEach((item) => {
      item.addEventListener('mouseenter', () => {
        const megaInItem = item.querySelector('.badass-mega-menu');
        const dropdownInItem = item.querySelector('details.mega-menu:not(.badass-mega-menu)');

        this.menus.forEach((menu) => {
          if (menu !== megaInItem && menu.open) {
            this.closeMenu(menu);
          }
        });

        this.dropdownMenus.forEach((menu) => {
          if (menu !== dropdownInItem && menu.open) {
            this.closeDropdown(menu);
          }
        });
      });
    });
  }

  initDropdownMenus() {
    if (!this.dropdownMenus.length) return;

    this.dropdownMenus.forEach((menu) => {
      const summary = menu.querySelector('summary');
      const submenu = menu.querySelector('.header__submenu');
      const headerMenu = menu.closest('header-menu');

      if (summary) {
        summary.addEventListener('click', (event) => {
          if (!this.supportsHover || event.detail === 0) return;
          event.preventDefault();
          if (menu.hasAttribute('open')) {
            this.closeDropdown(menu);
          } else {
            this.openDropdown(menu);
          }
        });
      }

      if (this.supportsHover) {
        const keepOpen = () => clearTimeout(this.closeTimers.get(menu));
        const tryClose = () => this.scheduleDropdownClose(menu);

        if (summary) {
          summary.addEventListener('mouseenter', () => this.openDropdown(menu));
          summary.addEventListener('mouseleave', tryClose);
        }

        if (submenu) {
          submenu.addEventListener('mouseenter', keepOpen);
          submenu.addEventListener('mouseleave', tryClose);
        }
      }

      menu.addEventListener('toggle', () => {
        if (summary) {
          summary.setAttribute('aria-expanded', menu.open ? 'true' : 'false');
        }

        if (menu.open) {
          this.closeOtherMenus(menu);
          this.closeOtherDropdowns(menu);
          this.setHeaderPreventHide(true);
        } else if (!this.anyMenuOpen()) {
          this.setHeaderPreventHide(false);
        }
      });

      if (headerMenu && !headerMenu.dataset.dropdownHoverPatched) {
        headerMenu.dataset.dropdownHoverPatched = 'true';
        const originalClose = headerMenu.close.bind(headerMenu);
        headerMenu.close = function () {
          if (menu.hasAttribute('open') && (menu.matches(':hover') || headerMenu.matches(':hover'))) {
            return;
          }
          originalClose();
        };
      }
    });
  }

  anyMenuOpen() {
    return (
      Array.from(this.menus).some((menu) => menu.open) ||
      Array.from(this.dropdownMenus).some((menu) => menu.open)
    );
  }

  initScroller(menu) {
    const scroller = menu.querySelector('.badass-mega-menu__scroller');
    const grid = menu.querySelector('.badass-mega-menu__grid--scroll');
    if (!scroller || !grid) return;

    const prev = scroller.querySelector('[data-mega-scroll="prev"]');
    const next = scroller.querySelector('[data-mega-scroll="next"]');
    const cardWidth = () => {
      const card = grid.querySelector('.badass-mega-menu__card');
      if (!card) return grid.clientWidth * 0.8;
      const styles = window.getComputedStyle(grid);
      const gap = parseFloat(styles.columnGap || styles.gap) || 20;
      return card.getBoundingClientRect().width + gap;
    };

    const onScrollOrResize = () => this.updateScroller(menu);
    const keepOpen = () => clearTimeout(this.closeTimers.get(menu));

    const scrollByCard = (direction) => {
      grid.scrollBy({ left: direction * cardWidth(), behavior: 'smooth' });
    };

    const bindNavBtn = (button, direction) => {
      if (!button) return;

      /* Keep focus on the menu trigger so Dawn's details focusout does not close the panel. */
      button.addEventListener('pointerdown', (event) => {
        event.preventDefault();
        event.stopPropagation();
        keepOpen();
      });

      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        keepOpen();
        if (button.getAttribute('aria-disabled') === 'true') return;
        scrollByCard(direction);
      });
    };

    bindNavBtn(prev, -1);
    bindNavBtn(next, 1);

    const headerMenu = menu.closest('header-menu');
    if (headerMenu && !headerMenu.dataset.badassMegaPatched) {
      headerMenu.dataset.badassMegaPatched = 'true';
      const originalClose = headerMenu.close.bind(headerMenu);
      headerMenu.close = function () {
        if (menu.hasAttribute('open') && (menu.matches(':hover') || headerMenu.matches(':hover'))) {
          return;
        }
        originalClose();
      };
    }

    let drag = null;
    const DRAG_THRESHOLD = 8;

    grid.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (event.target.closest('[data-mega-scroll]')) return;
      drag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startScroll: grid.scrollLeft,
        moved: false,
        captured: false,
      };
    });

    grid.addEventListener('pointermove', (event) => {
      if (!drag || event.pointerId !== drag.pointerId) return;
      const delta = event.clientX - drag.startX;
      if (!drag.moved && Math.abs(delta) < DRAG_THRESHOLD) return;
      if (!drag.moved) {
        drag.moved = true;
        grid.classList.add('is-dragging');
        try {
          grid.setPointerCapture(event.pointerId);
          drag.captured = true;
        } catch (err) {}
      }
      grid.scrollLeft = drag.startScroll - (event.clientX - drag.startX);
    });

    const endDrag = (event) => {
      if (!drag || event.pointerId !== drag.pointerId) return;
      const didMove = drag.moved;
      grid.classList.remove('is-dragging');
      if (drag.captured && grid.hasPointerCapture(event.pointerId)) {
        grid.releasePointerCapture(event.pointerId);
      }
      drag = didMove ? { moved: true } : null;
      window.setTimeout(() => {
        drag = null;
      }, 0);
    };

    grid.addEventListener('pointerup', endDrag);
    grid.addEventListener('pointercancel', endDrag);

    grid.addEventListener(
      'click',
      (event) => {
        if (drag && drag.moved) {
          event.preventDefault();
          event.stopPropagation();
        }
      },
      true
    );

    grid.addEventListener('scroll', onScrollOrResize, { passive: true });
    grid.addEventListener(
      'wheel',
      (event) => {
        if (grid.scrollWidth <= grid.clientWidth + 2) return;
        if (Math.abs(event.deltaY) < Math.abs(event.deltaX)) return;

        const atStart = grid.scrollLeft <= 0;
        const atEnd = grid.scrollLeft + grid.clientWidth >= grid.scrollWidth - 2;
        if ((event.deltaY < 0 && atStart) || (event.deltaY > 0 && atEnd)) return;

        event.preventDefault();
        grid.scrollLeft += event.deltaY;
      },
      { passive: false }
    );

    window.addEventListener('resize', onScrollOrResize);
    this.updateScroller(menu);
  }

  updateScroller(menu) {
    const scroller = menu.querySelector('.badass-mega-menu__scroller');
    const grid = menu.querySelector('.badass-mega-menu__grid--scroll');
    if (!scroller || !grid) return;

    const prev = scroller.querySelector('[data-mega-scroll="prev"]');
    const next = scroller.querySelector('[data-mega-scroll="next"]');
    const thumb = scroller.querySelector('[data-mega-progress]');
    const maxScroll = Math.max(grid.scrollWidth - grid.clientWidth, 0);
    const canScroll = maxScroll > 2;
    const atStart = grid.scrollLeft <= 2;
    const atEnd = grid.scrollLeft >= maxScroll - 2;

    scroller.classList.toggle('is-scrollable', canScroll);
    scroller.classList.toggle('is-at-start', atStart);
    scroller.classList.toggle('is-at-end', atEnd);

    const setBtnState = (button, inactive) => {
      if (!button) return;
      button.removeAttribute('disabled');
      button.setAttribute('aria-disabled', inactive ? 'true' : 'false');
      button.classList.toggle('is-disabled', inactive);
    };

    setBtnState(prev, !canScroll || atStart);
    setBtnState(next, !canScroll || atEnd);

    if (thumb && thumb.parentElement && grid.scrollWidth > 0) {
      const trackWidth = thumb.parentElement.clientWidth;
      const thumbWidth = Math.max((grid.clientWidth / grid.scrollWidth) * trackWidth, 48);
      const maxMove = Math.max(trackWidth - thumbWidth, 0);
      const progress = maxScroll > 0 ? grid.scrollLeft / maxScroll : 0;
      thumb.style.width = `${thumbWidth}px`;
      thumb.style.transform = `translateX(${progress * maxMove}px)`;
    }
  }

  updateHeaderPosition() {
    const headerWrapper = document.querySelector('.header-wrapper');
    const sectionHeader = document.querySelector('.section-header');
    const target = headerWrapper || sectionHeader;
    if (!target) return;

    // Use the painted header bottom — NOT header-group (inverse header is absolute
    // and does not expand the group box, which caused the panel to overlap nav links).
    const bottom = Math.ceil(target.getBoundingClientRect().bottom);
    const topValue = `${bottom}px`;

    document.documentElement.style.setProperty('--badass-mega-top', topValue);
    document.documentElement.style.setProperty('--header-bottom-position-desktop', topValue);
  }

  setHeaderPreventHide(prevent) {
    const header = document.querySelector('.header-wrapper');
    if (header) header.preventHide = prevent;
  }

  openMenu(menu) {
    clearTimeout(this.closeTimers.get(menu));
    menu.classList.add('badass-mega-menu--active');
    menu.setAttribute('open', '');
    this.updateHeaderPosition();
    requestAnimationFrame(() => {
      this.updateHeaderPosition();
      this.updateScroller(menu);
    });
    const summary = menu.querySelector('summary');
    if (summary) summary.setAttribute('aria-expanded', 'true');
    this.closeOtherMenus(menu);
    this.closeOtherDropdowns(menu);
    this.setHeaderPreventHide(true);
    document.body.classList.add('badass-mega-menu-open');
  }

  isMenuHovered(menu) {
    const summary = menu.querySelector('summary');
    const panel = menu.querySelector('.badass-mega-menu__panel');
    const headerMenu = menu.closest('header-menu');
    return Boolean(
      menu.matches(':hover') ||
      (headerMenu && headerMenu.matches(':hover')) ||
      (summary && summary.matches(':hover')) ||
      (panel && panel.matches(':hover'))
    );
  }

  scheduleClose(menu) {
    const timer = setTimeout(() => {
      if (this.isMenuHovered(menu)) return;
      this.closeMenu(menu);
    }, 280);
    this.closeTimers.set(menu, timer);
  }

  closeMenu(menu) {
    clearTimeout(this.closeTimers.get(menu));
    menu.classList.remove('badass-mega-menu--active');
    menu.removeAttribute('open');
    const summary = menu.querySelector('summary');
    if (summary) summary.setAttribute('aria-expanded', 'false');
    this.setHeaderPreventHide(false);
    if (!this.anyMenuOpen()) {
      document.body.classList.remove('badass-mega-menu-open');
    }
  }

  closeOtherMenus(activeMenu) {
    this.menus.forEach((menu) => {
      if (menu !== activeMenu && menu.open) {
        this.closeMenu(menu);
      }
    });
  }

  openDropdown(menu) {
    clearTimeout(this.closeTimers.get(menu));
    menu.setAttribute('open', '');
    const summary = menu.querySelector('summary');
    if (summary) summary.setAttribute('aria-expanded', 'true');
    this.closeOtherMenus(menu);
    this.closeOtherDropdowns(menu);
    this.setHeaderPreventHide(true);
  }

  closeDropdown(menu) {
    clearTimeout(this.closeTimers.get(menu));
    menu.removeAttribute('open');
    const summary = menu.querySelector('summary');
    if (summary) summary.setAttribute('aria-expanded', 'false');
    if (!this.anyMenuOpen()) {
      this.setHeaderPreventHide(false);
    }
  }

  scheduleDropdownClose(menu) {
    const timer = setTimeout(() => {
      if (this.isDropdownHovered(menu)) return;
      this.closeDropdown(menu);
    }, 280);
    this.closeTimers.set(menu, timer);
  }

  isDropdownHovered(menu) {
    const summary = menu.querySelector('summary');
    const submenu = menu.querySelector('.header__submenu');
    const headerMenu = menu.closest('header-menu');
    return Boolean(
      menu.matches(':hover') ||
        (headerMenu && headerMenu.matches(':hover')) ||
        (summary && summary.matches(':hover')) ||
        (submenu && submenu.matches(':hover'))
    );
  }

  closeOtherDropdowns(activeMenu) {
    this.dropdownMenus.forEach((menu) => {
      if (menu !== activeMenu && menu.open) {
        this.closeDropdown(menu);
      }
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new BadassMegaMenu());
} else {
  new BadassMegaMenu();
}
